<template>
  <section
    v-if="open"
    class="fixed bg-secondary/30 w-full h-full inset-0 z-50 overflow-y-auto xl:hidden"
  >
    <div class="bg-white absolute bottom-12 min-h-72 w-full md:h-full md:top-0 md:right-0 md:max-w-96">
      <div class="p-3 border-b border-b-gray-100">
        <h3 class="!text-gray-900 font-medium mb-2">
          Document Filters
        </h3>

        <span class="block text-xs leading-snug">You can filter the document to only show projects that are aligned with selected categories from a specific framework.</span>
      </div>

      <div class="relative">
        <!-- Frameworks Dropdown Toggle -->
        <button
          type="button"
          class="flex items-center justify-between text-xs uppercase font-medium hover:bg-secondary-subtle px-2 py-2 cursor-pointer duration-200 w-full"
          @click="showFrameworks = !showFrameworks"
        >
          <span class="flex items-center">
            <template v-if="selectedFramework">
              <img
                :src="selectedFramework.icon"
                :alt="`Icon for ${selectedFramework.name}`"
                class="block w-6 h-6 lg:w-8 lg:h-8"
              >

              <span class="block mb-0 leading-none text-center text-xs pl-3">
                {{ selectedFramework.shortName }}
              </span>
            </template>

            <template v-else>
              <img
                :src="NoFrameworkIcon"
                alt="Icon for No Framework"
                class="block w-6 h-6 lg:w-8 lg:h-8"
              >

              <span class="block mb-0 leading-none text-center text-xs pl-3">
                Framework
              </span>
            </template>
          </span>

          <i class="fa-solid fa-sharp fa-caret-down inline-block ml-1" />
        </button>

        <!-- Frameworks Dropdown Content -->
        <template v-if="showFrameworks">
          <div class="lg:absolute borber-b w-full cursor-pointer mb-10 bg-white lg:-left-[330px] top-0 z-20">
            <!-- No Framework view -->
            <div
              class="text-xs uppercase font-medium py-2 px-2 hover:bg-secondary-subtle border-b last-of-type:border-b-0 duration-200"
              @click="selectedFramework = null; showFrameworks = false"
            >
              <div class="flex items-center">
                <img
                  :src="NoFrameworkIcon"
                  :alt="`Icon for no Framework Icon`"
                  class="w-6 h-6 lg:w-8 lg:h-8"
                >

                <span class="ms-3">
                  No Framework
                </span>
              </div>
            </div>

            <!-- Frameworks Loop -->
            <template
              v-for="framework in frameworks"
              :key="framework.id"
            >
              <div
                class="text-xs uppercase font-medium py-2 px-2 lg:px-5 hover:bg-secondary-subtle border-b last-of-type:border-b-0 duration-200"
                @click="selectedFramework = framework; showFrameworks = false"
              >
                <div class="flex items-center">
                  <img
                    :src="framework.icon"
                    :alt="`Icon for ${framework.name}`"
                    class="w-6 h-6 lg:w-8 lg:h-8"
                  >

                  <span class="ms-3">
                    {{ framework.name }}
                  </span>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>

      <template v-if="selectedFramework">
        <div class="flex flex-col w-full bg-white border border-t-0 rounded-b">
          <div class="p-3 border-b border-b-gray-100 bg-slate-50">
            <button
              type="button"
              class="text-link-blue underline-offset-2 underline text-xs pr-4"
              @click="selectAllCategories"
            >
              Select All
            </button>

            <button
              type="button"
              class="text-link-blue underline-offset-2 underline text-xs"
              @click="deselectAllCategories"
            >
              Select None
            </button>
          </div>

          <div class="overflow-y-auto max-h-[55vh]">
            <ul class="pt-4 pb-6">
              <template v-if="categories && categories">
                <template
                  v-for="category in categories"
                  :key="category.id"
                >
                  <li
                    class="flex text-gray-800 justify-between items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 duration-150 transition-colors font-medium"
                    :class="[
                      {'!text-gray-400 font-medium hover:bg-transparent': !selectedCategories.includes(category.id)},
                    ]"
                    @click="toggleCategorySelection(category.id)"
                  >
                    <div class="flex items-center gap-2">
                      <CategoryIcon
                        :icon="category.icon"
                        :name="category.shortName"
                        :background-color="selectedCategories.includes(category.id) ? category.backgroundColor : '#9ca3af'"
                        class="[&_img]:!w-7 [&_img]:!h-7 flex-shrink-0"
                      />
                      <span class="text-xs">{{ category.shortName }}</span>
                    </div>

                    <input
                      type="checkbox"
                      class="pointer-events-none"
                      :checked="selectedCategories.includes(category.id)"
                    >
                  </li>
                </template>
              </template>

              <template v-else>
                <li class="px-4 py-1.5">
                  No Framework Selected
                </li>
              </template>
            </ul>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import { CategoryIcon } from '@/components/category/index.js';
import NoFrameworkIcon from '@/assets/images/graph/no-framework-icon.png';

defineProps({
  frameworks: {
    type: Array,
    default: () => [],
  },
  categories: {
    type: Array,
    default: () => [],
  },
  open: {
    type: Boolean,
    default: false,
  },
});

const showFrameworks = ref(false);
const selectedFramework = defineModel('selectedFramework', { type: Object, default: () => ({}) });
const selectedCategories = defineModel('selectedCategories', { type: Array, default: () => [] });
const categories = computed(() => { return selectedFramework.value.categories || []; });

// Method to toggle category selection
const toggleCategorySelection = categoryId => {
  if (selectedCategories.value.includes(categoryId)) {
    selectedCategories.value = selectedCategories.value.filter(selectedCategoryId => selectedCategoryId !== categoryId);
  } else {
    selectedCategories.value.push(categoryId);
  }
};

// Method to select all categories
const selectAllCategories = () => {
  selectedCategories.value = categories.value.map(category => category.id);
};

// Method to deselect all categories
const deselectAllCategories = () => {
  selectedCategories.value = [];
};

</script>
